import React, { useCallback, useEffect, useRef, useState } from 'react';
import Layout from '../components/Layout';
import { authService } from '../services/authService';
import { pipelineService } from '../services/pipelineService';
import { integrationService } from '../services/integrationService';
import { hasData as coverageHasData, defaultTab, importSummary } from '../lib/dataState';
import { useRunStream } from '../hooks/useRunStream';
import RunProgress from '../components/data/RunProgress';
import EnrichmentJobControls from '../components/data/EnrichmentJobControls';
import ImportOnboarding from '../components/data/ImportOnboarding';
import PipelinePanel from '../components/data/PipelinePanel';
import DataQualityPanel from '../components/data/DataQualityPanel';
import RunHistoryPanel from '../components/data/RunHistoryPanel';
import DataQualityExplainabilityPanel from '../components/DataQualityExplainabilityPanel';

const TEAL = '#00C6AE';

function Tabs({ tab, setTab, showPipeline }) {
  const item = (id, label) => {
    const active = tab === id;
    return (
      <button
        key={id}
        type="button"
        role="tab"
        aria-selected={active}
        onClick={() => setTab(id)}
        className={`px-4 py-2 -mb-px border-b-2 text-sm font-medium transition-colors ${
          active ? 'text-gray-900' : 'border-transparent text-gray-500 hover:text-gray-800'
        }`}
        style={active ? { borderColor: TEAL } : undefined}
      >
        {label}
      </button>
    );
  };
  return (
    <div role="tablist" className="flex gap-1 border-b border-gray-200">
      {item('quality', 'Quality')}
      {item('history', 'History')}
      {showPipeline && item('pipeline', 'Pipeline')}
    </div>
  );
}

export default function DataPage() {
  const [user, setUser] = useState(null);
  const [coverage, setCoverage] = useState([]);
  const [runs, setRuns] = useState([]);
  const [integrations, setIntegrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tab, setTab] = useState('quality');
  const [runKind, setRunKind] = useState('ingest');
  const [starting, setStarting] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [dqReloadKey, setDqReloadKey] = useState(0);
  // Active enrichment job: its id (also persisted so a paused job survives reload),
  // its durable status+per-step progress (polled from GET /runs/{id}/job), and the
  // "pausing…" transient between the click and the chunk boundary.
  const [jobId, setJobId] = useState(() => localStorage.getItem('sc.enrichmentJobId') || null);
  const [job, setJob] = useState(null);
  const [pausing, setPausing] = useState(false);

  const isAdmin = user?.role === 'admin';
  const hasData = coverageHasData(coverage);
  const hasDataRef = useRef(false);
  hasDataRef.current = hasData;
  const tabTouched = useRef(false);

  const loadCore = useCallback(async () => {
    const [cov, rs] = await Promise.all([
      pipelineService.getCoverage(),
      pipelineService.listRuns().catch(() => [])
    ]);
    setCoverage(cov || []);
    setRuns(rs || []);
    return cov || [];
  }, []);

  // When a run ends, pull the fresh numbers. The first successful import flips
  // the page from "import your data" to "here is its quality", which is the
  // whole point of the shift.
  const onFinish = useCallback(
    async (status) => {
      const wasEmpty = !hasDataRef.current;
      try {
        const cov = await loadCore();
        if (status === 'success') {
          setDqReloadKey((k) => k + 1);
          if (wasEmpty && coverageHasData(cov)) setTab('quality');
        }
      } catch (e) {
        setError(e.message);
      }
    },
    [loadCore]
  );

  const { state: runState, startRun, followExisting, reattached } = useRunStream({ onFinish });
  const busy = runState.status === 'running' || starting;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [u, cov] = await Promise.all([authService.getCurrentUser(), loadCore()]);
        if (cancelled) return;
        setUser(u);
        integrationService
          .getIntegrations()
          .then((list) => !cancelled && setIntegrations(list || []))
          .catch(() => {}); // an unreachable integrations API must not block the page
        if (!tabTouched.current) {
          setTab(defaultTab({ hasData: coverageHasData(cov), isAdmin: u?.role === 'admin' }));
        }
      } catch (e) {
        if (!cancelled) setError(e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [loadCore]);

  // Reattach to an import that was already running when the page loaded.
  const followedRef = useRef(false);
  useEffect(() => {
    if (followedRef.current || loading || !runs.length) return;
    const live = runs.find((r) => r.status === 'running');
    if (live) {
      followedRef.current = true;
      setRunKind('ingest');
      setDismissed(false);
      followExisting(live.run_id, new Date(live.started_at).getTime());
    }
  }, [runs, loading, followExisting]);

  const selectTab = (id) => {
    tabTouched.current = true;
    setTab(id);
  };

  const launch = useCallback(
    async (kind, starter) => {
      setError(null);
      setDismissed(false);
      setRunKind(kind);
      setStarting(true);
      try {
        await startRun(starter);
      } catch (e) {
        setError(e.message);
      } finally {
        setStarting(false);
      }
    },
    [startRun]
  );

  const startImport = () => launch('ingest', () => pipelineService.startIngest({ mode: 'full' }));
  const runEnrichment = (opts) =>
    launch('enrich', async () => {
      const id = await pipelineService.startEnrichment(opts);
      setJobId(id);
      localStorage.setItem('sc.enrichmentJobId', id);
      setPausing(false);
      return id;
    });

  // Rediscover an in-flight job on load from the SERVER (the source of truth), so a
  // running/paused run always shows — even if client-side state was lost. Runs once
  // on mount (the auth token persists across reloads); non-fatal if there is none.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const a = await pipelineService.getActiveJob();
        if (!cancelled && a && a.job_id) {
          setJobId(a.job_id);
          localStorage.setItem('sc.enrichmentJobId', a.job_id);
        }
      } catch {
        /* no active job / not ready — non-fatal */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Poll the DURABLE job status + per-step progress while a job is active, so the
  // bars survive a reload and a paused job shows its resume controls. Stops once the
  // job reaches a terminal state; a single transient error just retries (only give
  // up after several consecutive failures — the job is likely gone).
  useEffect(() => {
    if (!jobId) return undefined;
    let cancelled = false;
    let timer;
    let fails = 0;
    const tick = async () => {
      try {
        const j = await pipelineService.getJob(jobId);
        if (cancelled) return;
        fails = 0;
        setJob(j);
        if (j.status === 'running') setPausing(false); // a fresh/resumed run cleared the flag
        if (j.status === 'success' || j.status === 'failed' || j.status === 'cancelled') {
          localStorage.removeItem('sc.enrichmentJobId');
          setJobId(null);
          return; // terminal — stop polling
        }
      } catch {
        if (cancelled) return;
        if (++fails >= 3) {
          // Polling gave up (a 401 already redirected to login via the service; this
          // handles transient/network failures). Clear the job too, so we never leave a
          // frozen "running" panel with a ticking clock on screen.
          localStorage.removeItem('sc.enrichmentJobId');
          setJobId(null);
          setJob(null);
          return;
        }
      }
      timer = setTimeout(tick, 1000);
    };
    tick();
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [jobId]);

  const pauseJob = async () => {
    if (!jobId) return;
    setPausing(true);
    try {
      await pipelineService.pauseJob(jobId);
    } catch (e) {
      setPausing(false);
      setError(e.message);
    }
  };
  const cancelJob = async () => {
    if (!jobId) return;
    try {
      await pipelineService.cancelJob(jobId);
    } catch (e) {
      setError(e.message);
    }
  };
  const resumeJob = async () => {
    if (!jobId) return;
    setError(null);
    setPausing(false);
    setRunKind('enrich');
    setDismissed(false);
    try {
      await pipelineService.resumeJob(jobId);
      followExisting(jobId, Date.now()); // re-attach the SSE stream to the same job
    } catch (e) {
      setError(e.message);
    }
  };
  // One field enricher over its backlog (POST /runs/enrich). Same 'enrich' run kind:
  // it streams enrich:<plugin> steps, which RunProgress renders without a manifest.
  const runEnricher = (opts) => launch('enrich', () => pipelineService.startEnricher(opts));

  // An active enrichment job owns the single top-of-page status panel (rich, durable,
  // with controls). The generic SSE RunProgress falls back for everything else
  // (ingest runs, and the enrichment done/failed card once the job is terminal).
  const jobActive = job && (job.status === 'running' || job.status === 'paused');
  const showProgress = runState.status !== 'idle' && !dismissed;

  return (
    <Layout user={user}>
      <div className="space-y-6">
        <header>
          <h1 className="text-2xl font-semibold text-gray-900">Data</h1>
          <p className="text-sm text-gray-500 mt-1">
            {hasData
              ? 'How complete and trustworthy your records are — and the pipeline that keeps them that way.'
              : 'Import your ATS records to see how complete and trustworthy they are.'}
          </p>
        </header>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-xl px-4 py-3">{error}</div>
        )}

        {jobActive ? (
          <EnrichmentJobControls
            job={job}
            onPause={pauseJob}
            onResume={resumeJob}
            onCancel={cancelJob}
            pausing={pausing}
          />
        ) : (
          showProgress && (
            <RunProgress
              state={runState}
              kind={runKind}
              reattached={reattached}
              summary={runKind === 'ingest' ? importSummary(coverage) : ''}
              onDismiss={() => setDismissed(true)}
            />
          )
        )}

        {loading ? (
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-8 text-sm text-gray-500">
            Loading…
          </div>
        ) : !hasData ? (
          // No data: the page is the import screen. No tabs to wander into.
          runState.status === 'running' ? null : (
            <ImportOnboarding
              integrations={integrations}
              onStartImport={startImport}
              starting={starting}
              isAdmin={isAdmin}
              disabled={busy}
            />
          )
        ) : (
          <>
            <Tabs tab={tab} setTab={selectTab} showPipeline={isAdmin} />
            {(tab === 'quality' || tab === 'history') && (
              <div className="mb-6">
                <DataQualityExplainabilityPanel tab={tab} />
              </div>
            )}
            {tab === 'quality' && (
              <DataQualityPanel
                coverage={coverage}
                isAdmin={isAdmin}
                reloadKey={dqReloadKey}
                onRunEnrichment={runEnrichment}
                busy={busy}
                job={job}
              />
            )}
            {tab === 'history' && <RunHistoryPanel />}
            {tab === 'pipeline' && (
              <PipelinePanel
                runs={runs}
                coverage={coverage}
                onStartImport={startImport}
                onRunEnrichment={runEnrichment}
                onRunEnricher={runEnricher}
                busy={busy}
                job={job}
              />
            )}
          </>
        )}
      </div>
    </Layout>
  );
}
